import React, { useEffect, useMemo } from "react";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";

import { useActivities } from "../../../context/ActivitiesContext";
import { API_GAME } from "../../../constants/api";
import * as actions from "../../../store/actions";
import DashboardCard from "../DashboardCard";
import LinkProfessionalBanner from "../LinkProfessionalBanner";
import DifficultyBadge from "../../../features/NewActivities/DifficultyBadge/DifficultyBadge";
import AMedalStatus from "../../../features/NewActivities/AMedalStatus/AMedalStatus";
import { flags } from "../../../features/NewActivities/ACard/Flags";
import EmptyState from "../../../components/EmptyState/Index";
import afinandinho_empty from "../../../assets/images/no_tasks.svg";
import useFetch from "../../../hooks/useFetch";
import { API_Q_PARENT } from "../../../constants/api";

const MAX_GAMES = 3;
const MAX_GAMES_MOBILE = 2;

// Grid 3 colunas (igual ao RecommendedGamesSection)
const GamesGrid = styled(Box)(({ $isMobile }) => ({
  display: "grid",
  gridTemplateColumns: `repeat(${$isMobile ? MAX_GAMES_MOBILE : MAX_GAMES}, 1fr)`,
  gap: "1.5rem",
  width: "100%",
  overflow: "hidden",
}));

// Container de cada card individual
const GameCard = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  cursor: "pointer",
  minWidth: 0,
  border: "1.5px solid #D8D8D8",
  borderRadius: "12px",
  padding: "8px",
});

// Imagem do jogo
const GameImage = styled("img")({
  width: "100%",
  objectFit: "cover",
  borderRadius: "0.55rem",
  transition: "transform 0.2s ease",
  "&:hover": {
    transform: "scale(1.05)",
  },
});

// Área inferior: badge de dificuldade + ícone troféu
const GameFooter = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  paddingTop: "34px",
  paddingBottom: "34px",
  height: "50px",
});

const MyActivitiesCard = ({ currentUser }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { activities, setInfoActivity } = useActivities();
  const { tasks } = useSelector((state) => state.tasks);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { session, activeUser } = useSelector((state) => state.session);

  const isViewingAsPatient = !!activeUser;
  const parentSub = session?.my_user?.parent_sub;
  const hasLink = !!parentSub || isViewingAsPatient;
  const [myParentUser, isLoadingParent, , fetchMyParentUser] = useFetch({
    autoInit: false,
  });

  const myUser = session?.my_user;
  // const activeUser = session?.activeUser;
  const isFrozen =
    myUser?.parent_activity_status === "false" ||
    activeUser?.parent_activity_status === "false";

  useEffect(() => {
    if (parentSub && !isViewingAsPatient) {
      fetchMyParentUser({
        url: API_Q_PARENT,
      });
    }
  }, [parentSub, isViewingAsPatient, fetchMyParentUser]);

  const professionalName = isViewingAsPatient
    ? session?.my_user?.given_name // o próprio profissional logado
    : isLoadingParent
      ? " "
      : (myParentUser?.given_name ?? "Não existe vinculo");

  useEffect(() => {
    if (session && !tasks) {
      dispatch(actions.initFetchTasks(activeUser));
    }
  }, [session, tasks, activeUser, dispatch]);

  const myActivities = useMemo(() => {
    if (!activities || !tasks) return [];

    return [...tasks]
      .sort((a, b) => new Date(b.date_created) - new Date(a.date_created))
      .map((task) =>
        activities.find(
          (activity) => String(activity.id) === String(task.game_id),
        ),
      )
      .filter(Boolean);
  }, [tasks, activities]);

  const medalsByGameId = useMemo(() => {
    const medalsMap = new Map();

    if (!Array.isArray(currentUser?.medals)) return medalsMap;

    currentUser.medals.forEach((medal) => {
      const gameId = medal.game_id?.[0];
      if (gameId == null) return;

      const normalizedGameId = String(gameId);

      // Preserva o comportamento anterior do find, que retornava a primeira medalha.
      if (!medalsMap.has(normalizedGameId)) {
        medalsMap.set(normalizedGameId, medal);
      }
    });

    return medalsMap;
  }, [currentUser?.medals]);

  const handleGameClick = (game) => {
    setInfoActivity(game);
  };

  const getGameImage = (game) => {
    return game.type === "jogo"
      ? `${API_GAME}/${game.path}/jogo_imagem_.jpg`
      : `${API_GAME}/${game.path}/audio_imagem_.jpg`;
  };

  if (!myActivities.length || isFrozen || !hasLink) {
    return (
      <>
        {!hasLink && <LinkProfessionalBanner />}
        <DashboardCard title={t("global.my_activities")}>
          <EmptyState
            image={afinandinho_empty}
            altText="Sem atividades"
            text="Você está sem atividades"
            description={
              hasLink ? (
                <>
                  {" "}
                  Peça para <strong>{professionalName}</strong> adicionar
                  atividades para você treinar!
                </>
              ) : (
                <>Vincule-se a alguém para receber novos treinos!</>
              )
            }
          />
        </DashboardCard>
      </>
    );
  }

  function GameItem({ game }) {
    const medal = medalsByGameId.get(String(game.id));

    if (!medal)
      return (
        <>
          {flags[game.language] && game.language !== "Português" ? (
            <Box
              component="img"
              src={flags[game.language]}
              alt={game.language}
              sx={{ width: 26, height: 26 }}
            />
          ) : null}
        </>
      );
    else return <AMedalStatus medal={medal} visible={false} />;
  }

  return (
    <DashboardCard title={t("global.my_activities")}>
      <GamesGrid $isMobile={isMobile}>
        {myActivities.map((game) => (
          <GameCard key={game.id} onClick={() => handleGameClick(game)}>
            <GameImage
              src={getGameImage(game)}
              alt={game.name}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
            <GameFooter>
              <DifficultyBadge activity={game} />
              <GameItem game={game} />
            </GameFooter>
          </GameCard>
        ))}
      </GamesGrid>
    </DashboardCard>
  );
};

export default MyActivitiesCard;
